import { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const courses = [
  {
    icon: "ri-heart-pulse-line",
    title: "Biomagnetismo Médico",
    badge: "Más Popular",
    badgeColor: "bg-[#D4AF37] text-[#0A1F44]",
    modality: "Online / Presencial",
    duration: "40 horas",
    desc: "Aprende a equilibrar el pH del cuerpo mediante pares biomagnéticos para eliminar patógenos y restaurar la salud desde su origen energético.",
    benefits: ["Técnica avalada científicamente", "Certificación oficial", "Práctica supervisada", "Material didáctico incluido"],
    img: "https://public.readdy.ai/ai/img_res/792e109352a5d60934737693075ddc01.jpg",
  },
  {
    icon: "ri-brain-line",
    title: "Método Yuen",
    badge: "Diplomado",
    badgeColor: "bg-[#0A1F44] text-white",
    modality: "Online",
    duration: "30 horas",
    desc: "Técnica de kinesiología cuántica que identifica y elimina debilidades en el sistema nervioso para resolver bloqueos físicos, emocionales y mentales.",
    benefits: ["Resultados inmediatos", "Aplicable a distancia", "Sin contraindicaciones"],
    img: "https://readdy.ai/api/search-image?query=quantum%20kinesiology%20brain%20nervous%20system%20energy%20healing%20meditation%20professional%20training%20course%20golden%20light%20dark%20blue%20background&width=600&height=400&seq=course002&orientation=landscape",
  },
  {
    icon: "ri-mental-health-line",
    title: "Hipnosis Terapéutica",
    badge: "Certificación",
    badgeColor: "bg-[#D4AF37] text-[#0A1F44]",
    modality: "Online / Presencial",
    duration: "35 horas",
    desc: "Domina las técnicas de hipnosis clínica para reprogramar el subconsciente, eliminar fobias, adicciones y patrones limitantes de forma profunda.",
    benefits: ["Técnicas avanzadas", "Casos clínicos reales", "Supervisión personalizada", "Certificado internacional"],
    img: "https://readdy.ai/api/search-image?query=hypnotherapy%20therapeutic%20hypnosis%20mind%20reprogramming%20subconscious%20healing%20professional%20training%20elegant%20dark%20atmosphere%20golden%20light&width=600&height=400&seq=course003&orientation=landscape",
  },
  {
    icon: "ri-pulse-line",
    title: "Biotermodinamica Estructural",
    badge: "Taller",
    badgeColor: "bg-[#0A1F44] text-white",
    modality: "Presencial",
    duration: "20 horas",
    desc: "Trabaja con el campo energético del cuerpo humano para liberar bloqueos emocionales, activar la autocuración y elevar la frecuencia vibratoria.",
    benefits: ["Experiencia vivencial", "Grupo reducido", "Herramientas prácticas", "Seguimiento post-taller"],
    img: "https://readdy.ai/api/search-image?query=bioenergetics%20human%20energy%20field%20aura%20healing%20body%20vibration%20frequency%20sacred%20geometry%20golden%20blue%20light%20professional%20workshop&width=600&height=400&seq=course004&orientation=landscape",
  },
  {
    icon: "ri-dna-line",
    title: "Leyes Biológicas",
    badge: "Nuevo",
    badgeColor: "bg-emerald-600 text-white",
    modality: "Online",
    duration: "25 horas",
    desc: "Comprende las 5 leyes biológicas de la naturaleza para entender el origen emocional de las enfermedades y activar procesos de sanación natural.",
    benefits: ["Visión revolucionaria", "Base científica sólida", "Aplicación clínica", "Comunidad de práctica"],
    img: "https://readdy.ai/api/search-image?query=biological%20laws%20nature%20healing%20DNA%20cell%20regeneration%20science%20energy%20medicine%20professional%20course%20dark%20blue%20golden%20background&width=600&height=400&seq=course005&orientation=landscape",
  },
  {
    icon: "ri-sparkling-line",
    title: "Fenómeno Tumoral de Biomagnetismo",
    badge: "Diplomado",
    badgeColor: "bg-[#0A1F44] text-white",
    modality: "Online / Presencial",
    duration: "50 horas",
    desc: "Identificación de las combinaciones del fenómeno tumoral del Dr Goiz.",
    benefits: ["Transformación integral", "Mentoring incluido", "Comunidad exclusiva", "Certificación premium"],
    img: "https://readdy.ai/api/search-image?query=personal%20development%20consciousness%20expansion%20meditation%20spiritual%20growth%20golden%20light%20sacred%20geometry%20human%20silhouette%20premium%20dark%20background&width=600&height=400&seq=course006&orientation=landscape",
  },
];

export default function Courses() {
  const { ref, inView } = useInView();
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section id="cursos" className="py-20 md:py-32 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-14 md:mb-20 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Programas de Formación</span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0A1F44] mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Cursos y Diplomados
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            Domina las técnicas que transforman realidades. Formación profesional con certificación y acompañamiento personalizado.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {courses.map((course, i) => (
            <div
              key={course.title}
              className={`group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#D4AF37]/40 transition-all duration-500 ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={course.img}
                  alt={course.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/60 to-transparent"></div>
                <span className={`absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full ${course.badgeColor}`}>
                  {course.badge}
                </span>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex-shrink-0">
                    <i className={`${course.icon} text-base`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0A1F44]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {course.title}
                  </h3>
                </div>

                <div className="flex items-center gap-4 mb-3">
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-computer-line"></i> {course.modality}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-time-line"></i> {course.duration}
                  </span>
                </div>

                <p className="text-gray-600 text-sm leading-relaxed mb-4">{course.desc}</p>

                {/* Benefits toggle */}
                <button
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  className="text-[#D4AF37] text-sm font-semibold flex items-center gap-1 mb-3 cursor-pointer hover:text-[#c9a42e] transition-colors"
                >
                  {expanded === i ? "Ocultar beneficios" : "Ver beneficios"}
                  {expanded === i ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>}
                </button>

                {expanded === i && (
                  <ul className="mb-4 space-y-1.5">
                    {course.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#D4AF37] flex-shrink-0"></i>
                        {b}
                      </li>
                    ))}
                  </ul>
                )}

                <a
                  href={`https://wa.me/523325077743?text=Hola,%20me%20interesa%20el%20curso%20de%20${encodeURIComponent(course.title)}%20del%20Instituto%20Ashaika`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#0A1F44] text-white text-sm font-semibold py-3 rounded-xl hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Solicitar Información
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-gray-500 text-sm mb-4">¿No encuentras lo que buscas? Tenemos más programas disponibles.</p>
          <a
            href="https://wa.me/523325077743?text=Hola,%20quiero%20conocer%20todos%20los%20programas%20disponibles%20en%20el%20Instituto%20Ashaika"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] font-semibold px-7 py-3 rounded-full hover:bg-[#0A1F44] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line"></i>
            Ver todos los programas
          </a>
        </div>
      </div>
    </section>
  );
}
